import React from 'react';
import type { LayerRenderer } from '../../../types';
import { FILL_STYLES, LINE_STYLES, getLineDashArray } from '../../../layers/symbols';
import { ColorField, PropertySection, SliderField } from './SymbolProperties';

/** Polygon symbology: fill + outline, with a live swatch preview. */
export default function PolygonSymbolEditor({
  renderer,
  onChange,
}: {
  renderer: LayerRenderer;
  onChange: (changes: Partial<LayerRenderer>) => void;
}) {
  const fillStyle = renderer.fillStyle ?? 'solid';
  const outlineStyle = renderer.outlineStyle ?? 'solid';
  const effectiveFillOpacity = fillStyle === 'transparent' ? 0 : renderer.fillOpacity;

  return (
    <>
      <PropertySection title="Fill">
        <div className="mb-2 grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
          {FILL_STYLES.map((style) => (
            <button
              key={style.id}
              type="button"
              aria-pressed={fillStyle === style.id}
              onClick={() => onChange({ fillStyle: style.id })}
              className={`rounded-md px-2 py-1 text-[11px] font-medium transition ${
                fillStyle === style.id ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'
              }`}
            >
              {style.name}
            </button>
          ))}
        </div>

        {renderer.type === 'single' && (
          <ColorField label="Colour" value={renderer.color} onChange={(color) => onChange({ color })} />
        )}
        <SliderField
          label="Opacity"
          value={Math.round(renderer.fillOpacity * 100)}
          min={0}
          max={100}
          suffix="%"
          // Editing opacity implies a visible fill.
          onChange={(value) => onChange({ fillOpacity: value / 100, fillStyle: 'solid' })}
        />
      </PropertySection>

      <PropertySection title="Outline">
        <ColorField
          label="Colour"
          value={renderer.outlineColor}
          onChange={(outlineColor) => onChange({ outlineColor })}
        />
        <SliderField
          label="Width"
          value={renderer.outlineWidth}
          min={0}
          max={10}
          step={0.5}
          suffix="px"
          onChange={(outlineWidth) => onChange({ outlineWidth })}
        />
        <label className="block py-1.5">
          <span className="text-xs text-slate-600">Style</span>
          <select
            value={outlineStyle}
            onChange={(event) =>
              onChange({ outlineStyle: event.target.value as LayerRenderer['outlineStyle'] })
            }
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400"
          >
            {LINE_STYLES.map((style) => (
              <option key={style.id} value={style.id}>
                {style.name}
              </option>
            ))}
          </select>
        </label>
      </PropertySection>

      <PropertySection title="Preview">
        <div className="flex h-24 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
          <svg viewBox="0 0 120 60" className="h-16 w-40">
            <rect
              x="6"
              y="6"
              width="108"
              height="48"
              rx="4"
              fill={renderer.color}
              fillOpacity={effectiveFillOpacity}
              stroke={renderer.outlineColor}
              strokeWidth={renderer.outlineWidth}
              strokeDasharray={getLineDashArray(outlineStyle)}
            />
          </svg>
        </div>
      </PropertySection>
    </>
  );
}
